<template>
  <Drawer
    :open="open"
    :title="t('passengers.title')"
    :subtitle="t('passengers.subtitle')"
    @close="$emit('close')"
  >
    <div class="fg-pax">
      <div v-for="band in bands" :key="keyOf(band)" class="fg-pax__row">
        <span class="fg-pax__label">
          {{ labelOf(band) }}
          <small class="fg-pax__band">{{ rangeOf(band) }}</small>
        </span>
        <div class="fg-counter">
          <button
            type="button"
            class="fg-counter__btn"
            :disabled="!canDecrement(band)"
            @click="setCount(band, countOf(band) - 1)"
          ><Icon name="minus" :size="14" /></button>
          <span class="fg-counter__value">{{ countOf(band) }}</span>
          <button
            type="button"
            class="fg-counter__btn"
            :disabled="!canIncrement(band)"
            @click="setCount(band, countOf(band) + 1)"
          ><Icon name="plus" :size="14" /></button>
        </div>
      </div>

      <section v-if="hasAgeSelectors" class="fg-pax__ages">
        <h3 class="fg-pax__ages-title">{{ t('passengers.agesTitle') }}</h3>

        <div v-if="children.length" class="fg-pax__age-group">
          <div class="fg-pax__age-group-header">{{ t('passengers.childrenAges') }}</div>
          <div class="fg-pax__age-grid">
            <label v-for="(child, i) in children" :key="`c-${i}`" class="fg-field">
              <span class="fg-field__label">{{ t('passengers.child') }} {{ i + 1 }}</span>
              <select :value="child.age" @change="updateChildAge(i, $event)">
                <option v-for="age in childRange" :key="age" :value="age">{{ age }} {{ t('passengers.years') }}</option>
              </select>
            </label>
          </div>
        </div>

        <div v-if="babies.length" class="fg-pax__age-group">
          <div class="fg-pax__age-group-header">{{ t('passengers.babyAges') }}</div>
          <div class="fg-pax__age-grid">
            <label v-for="(baby, i) in babies" :key="`b-${i}`" class="fg-field">
              <span class="fg-field__label">{{ t('passengers.baby') }} {{ i + 1 }}</span>
              <select :value="baby.age" @change="updateBabyAge(i, $event)">
                <option v-for="age in babyRange" :key="age" :value="age">{{ age }} {{ t('passengers.years') }}</option>
              </select>
            </label>
          </div>
        </div>
      </section>

      <section class="fg-pax__pets">
        <button
          v-if="!showPets"
          type="button"
          class="fg-pax__pets-link"
          @click="showPets = true"
        >{{ t('passengers.travelWithPets') }}</button>

        <template v-else>
          <h3 class="fg-pax__ages-title">{{ t('passengers.petsSection') }}</h3>
          <div v-for="kind in PET_KINDS" :key="kind.field" class="fg-pax__row">
            <span class="fg-pax__label">{{ t(kind.labelKey) }}</span>
            <div class="fg-counter">
              <button
                type="button"
                class="fg-counter__btn"
                :disabled="store.search.pets[kind.field] <= 0"
                @click="decPet(kind.field)"
              ><Icon name="minus" :size="14" /></button>
              <span class="fg-counter__value">{{ store.search.pets[kind.field] }}</span>
              <button
                type="button"
                class="fg-counter__btn"
                @click="incPet(kind.field)"
              ><Icon name="plus" :size="14" /></button>
            </div>
          </div>
        </template>
      </section>
    </div>

    <template #footer>
      <span class="fg-drawer__count">
        {{ t('passengers.total') }} · {{ totalCount }}
        <template v-if="totalPets > 0"> · {{ totalPets }} {{ t('passengers.petsSection').toLowerCase() }}</template>
      </span>
      <button class="fg-btn fg-btn--primary" type="button" @click="$emit('close')">{{ t('common.confirm') }}</button>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useStore } from '../../store'
import { useI18n } from '../../i18n'
import {
  countPassengers,
  countPets,
  paxBandLabel,
  rebuildPassengers as rebuildPassengersRule,
  resolvePaxBands,
  type PaxBand,
  type RebuildKindConfig,
} from '../../../../shared/rules'
import { PassengerType } from '../../../../shared/types/enums'
import type { Animal } from '../../../../shared/types'
import { humanizeLabel } from '../../utils/format'
import Drawer from '../Drawer.vue'
import Icon from '../Icon.vue'

type AnimalKind = keyof Animal

/** Libellé i18n d'une tranche : son rang normalisé, sinon son type. */
const TIER_LABEL: Record<string, string> = {
  junior: 'passengers.junior',
  senior: 'passengers.senior',
}
const TYPE_LABEL: Record<string, string> = {
  Adult: 'passengers.adults',
  Child: 'passengers.children',
  Baby: 'passengers.babies',
}

/**
 * Plafond par TYPE, pas par tranche : avec les 3 tranches adultes d'Armas, un
 * plafond par ligne autoriserait 27 adultes dans un dossier.
 */
const MAX_PER_TYPE = 9

interface PetConfig { field: AnimalKind; labelKey: string }
const PET_KINDS: readonly PetConfig[] = [
  { field: 'cats', labelKey: 'animals.cats' },
  { field: 'dogs', labelKey: 'animals.dogs' },
]

const props = defineProps<{ open: boolean }>()
defineEmits<{ close: [] }>()

const store = useStore()
const { t } = useI18n()

const counts = computed(() => countPassengers(store.search.passengers))
const totalCount = computed(() => counts.value.total)
const totalPets = computed(() => countPets(store.search.pets))

const children = computed(() => store.search.passengers.filter((p) => p.type === PassengerType.Child))
const babies = computed(() => store.search.passengers.filter((p) => p.type === PassengerType.Baby))
const hasAgeSelectors = computed(() => children.value.length > 0 || babies.value.length > 0)

// UNE LIGNE PAR TRANCHE TARIFAIRE du transporteur — Armas facture séparément
// Jeunesse (12-25) et Senior (60-120). Le découpage vient du backend
// (`pax_types`) ; le widget ne code plus ni types ni `param` en dur.
const bands = computed<PaxBand[]>(() => resolvePaxBands(store.search.journeys))

const childRange = computed(() => ageOptions(PassengerType.Child))
const babyRange = computed(() => ageOptions(PassengerType.Baby))

function ageOptions(type: PassengerType): number[] {
  const scoped = bands.value.filter((b) => b.type === type)
  if (scoped.length === 0) return []
  const min = Math.min(...scoped.map((b) => b.min))
  const max = Math.max(...scoped.map((b) => b.max))
  return Array.from({ length: max - min + 1 }, (_, i) => min + i)
}

function keyOf(band: PaxBand): string {
  return `${band.type}-${band.param}-${band.min}`
}

function labelOf(band: PaxBand): string {
  const label = paxBandLabel(band, bands.value)
  if ('tier' in label) return t(TIER_LABEL[label.tier])
  if ('name' in label) return humanizeLabel(label.name)
  return t(TYPE_LABEL[label.type] ?? 'passengers.adults')
}

/** Borne haute muette sur la tranche ouverte : c'est un plafond technique. */
function rangeOf(band: PaxBand): string {
  const range = band.max >= 99 ? `${band.min}+` : `${band.min}-${band.max}`
  return `${range} ${t('passengers.years')}`
}

function countOf(band: PaxBand): number {
  return store.search.passengers.filter((p) => p.type === band.type && p.param === band.param).length
}

/** Le dernier adulte du dossier n'est pas retirable, quelle que soit sa tranche. */
function canDecrement(band: PaxBand): boolean {
  if (countOf(band) <= 0) return false
  return band.type !== PassengerType.Adult || counts.value.adults > 1
}

function canIncrement(band: PaxBand): boolean {
  // Un bébé voyage sur les genoux d'un adulte — jamais plus de bébés que d'adultes.
  if (band.type === PassengerType.Baby) return counts.value.babies < counts.value.adults
  const total = band.type === PassengerType.Adult ? counts.value.adults : counts.value.children
  return total < MAX_PER_TYPE
}

// Section animaux : auto-ouverte si déjà des animaux, sinon repliée derrière le lien.
const showPets = ref(totalPets.value > 0)

// Chaque tranche est reconstruite indépendamment : l'appariement se fait sur
// `type` + `param`, donc un Senior ne se transforme jamais en Jeunesse.
const REBUILD_KINDS = computed<readonly RebuildKindConfig[]>(() => bands.value.map((band) => ({
  key: keyOf(band),
  type: band.type as PassengerType,
  param: band.param,
  defaultAge: () => band.defaultAge,
})))

function targetCounts(): Record<string, number> {
  return Object.fromEntries(bands.value.map((band) => [keyOf(band), countOf(band)]))
}

// Chaque ouverture repart d'une sélection VIERGE : 1 adulte (tranche standard),
// 0 partout ailleurs. Sans ça, les passagers d'une traversée précédente
// restaient — avec des `param` et des âges étrangers au nouveau transporteur.
watch(() => props.open, (isOpen) => {
  if (!isOpen) return
  showPets.value = totalPets.value > 0

  const adults = bands.value.filter((b) => b.type === PassengerType.Adult)
  const main = adults.find((b) => b.tier === 'standard') ?? adults[0]
  const target = Object.fromEntries(bands.value.map((band) => [keyOf(band), 0]))
  if (main) target[keyOf(main)] = 1

  store.search.passengers = rebuildPassengersRule([], target, REBUILD_KINDS.value)
})

function setCount(band: PaxBand, value: number): void {
  store.search.passengers = rebuildPassengersRule(
    store.search.passengers,
    { ...targetCounts(), [keyOf(band)]: Math.max(0, value) },
    REBUILD_KINDS.value,
  )
}

function updateChildAge(idx: number, event: Event): void {
  updateAgeAt(PassengerType.Child, idx, Number((event.target as HTMLSelectElement).value))
}

function updateBabyAge(idx: number, event: Event): void {
  updateAgeAt(PassengerType.Baby, idx, Number((event.target as HTMLSelectElement).value))
}

function updateAgeAt(type: PassengerType, idx: number, age: number): void {
  let seen = 0
  for (const pax of store.search.passengers) {
    if (pax.type !== type) continue
    if (seen === idx) { pax.age = age; return }
    seen++
  }
}

function incPet(field: AnimalKind): void {
  store.search.pets[field] = store.search.pets[field] + 1
}

function decPet(field: AnimalKind): void {
  if (store.search.pets[field] > 0) store.search.pets[field] = store.search.pets[field] - 1
}
</script>
